import type {
  SearchResult, DatasetItem, SourceItem,
  DataObjectItem, FlowItem, ConnectionItem,
} from '@/components/SpotlightSearch/types';
import { ModelSortDirection } from '@/api';
import { graphql } from '@/utils/graphql';
import { logger } from '@/utils/logger';
import {
  parseCloudResourceInfo,
  getDatasetQualifiedName,
  getSourceTableQualifiedName,
} from '@/components/SpotlightSearch/utils';

const ns = 'app.services.spotlight';

export default class Spotlight {
  static async fetchAll(workspaceId: string): Promise<SearchResult[]> {
    logger.debug({ label: `${ns}.fetchAll`, message: workspaceId });

    const [datasets, sourceTables, flows, connections] = await Promise.all([
      Spotlight.fetchDatasetsWithColumnsAndQueries(workspaceId),
      Spotlight.fetchSourceTablesWithColumns(workspaceId),
      Spotlight.fetchFlows(workspaceId),
      Spotlight.fetchConnections(workspaceId),
    ]);

    return [...datasets, ...sourceTables, ...flows, ...connections];
  }

  private static async fetchDatasetsWithColumnsAndQueries(workspaceId: string): Promise<SearchResult[]> {
    const items: SearchResult[] = [];
    const datasetsResult = await graphql('listDatasetsByWorkspace', { workspaceId });
    if (!datasetsResult?.items) return items;

    const datasets = datasetsResult.items as DatasetItem[];

    // Fetch SQL queries for all datasets in parallel
    const sqlQueryPromises = datasets.map(async (dataset) => {
      const sqlQueryResult = await graphql('sqlQueriesByDataset', {
        datasetId: dataset.id,
        sortDirection: ModelSortDirection.DESC,
        limit: 1,
      });
      return {
        datasetId: dataset.id,
        sqlQuery: sqlQueryResult?.items?.[0]?.sqlQuery || null,
      };
    });

    const sqlQueryResults = await Promise.all(sqlQueryPromises);
    const sqlQueryMap = new Map(
      sqlQueryResults.map(({ datasetId, sqlQuery }) => [datasetId, sqlQuery]),
    );

    for (const dataset of datasets) {
      if (!dataset.name || !dataset.viewName || !dataset.workspace?.seq) continue;

      const qualifiedName = getDatasetQualifiedName(dataset.workspace.seq, dataset.viewName);
      const sqlQuery = sqlQueryMap.get(dataset.id) || undefined;

      items.push({
        id: dataset.id,
        type: 'dataset',
        name: dataset.name,
        context: 'Dataset',
        viewName: qualifiedName,
      });

      if (sqlQuery) {
        items.push({
          id: `${dataset.id}-query`,
          type: 'query',
          name: sqlQuery,
          context: dataset.name,
          parentId: dataset.id,
          parentType: 'dataset',
          viewName: qualifiedName,
          sqlQuery,
        });
      }

      const info = parseCloudResourceInfo(dataset.cloudResourceInfo);
      if (info?.columns) {
        for (const col of info.columns) {
          if (col.id) {
            items.push({
              id: `${dataset.id}-col-${col.id}`,
              type: 'column',
              name: col.id,
              context: dataset.name,
              parentId: dataset.id,
              parentType: 'dataset',
              viewName: `${qualifiedName}.${col.id}`,
            });
          }
        }
      }
    }

    return items;
  }

  private static async fetchSourceTablesWithColumns(workspaceId: string): Promise<SearchResult[]> {
    const items: SearchResult[] = [];
    const sourcesResult = await graphql('listSourcesByWorkspace', { workspaceId });
    if (!sourcesResult?.items) return items;

    const sources = sourcesResult.items as SourceItem[];

    const dataObjectPromises = sources.map(async (source) => {
      const dataObjectsResult = await graphql('listDataObjectsBySource', { sourceId: source.id });
      return { source, dataObjects: dataObjectsResult?.items as DataObjectItem[] | undefined };
    });

    const sourceDataObjects = await Promise.all(dataObjectPromises);

    for (const { source, dataObjects } of sourceDataObjects) {
      if (!dataObjects) continue;
      for (const dataObj of dataObjects) {
        const workspaceSeq = dataObj.source?.workspace?.seq;
        if (!dataObj.name || !dataObj.viewName || !workspaceSeq) continue;

        const qualifiedName = getSourceTableQualifiedName(workspaceSeq, dataObj.viewName, dataObj.cloudResourceId);

        items.push({
          id: dataObj.id,
          type: 'sourceTable',
          name: dataObj.name,
          context: source.name,
          viewName: qualifiedName,
        });

        const info = parseCloudResourceInfo(dataObj.cloudResourceInfo);
        if (info?.columns) {
          for (const col of info.columns) {
            if (col.id) {
              items.push({
                id: `${dataObj.id}-col-${col.id}`,
                type: 'column',
                name: col.id,
                context: dataObj.name,
                parentId: dataObj.id,
                parentType: 'sourceTable',
                viewName: `${qualifiedName}.${col.id}`,
              });
            }
          }
        }
      }
    }

    return items;
  }

  private static async fetchFlows(workspaceId: string): Promise<SearchResult[]> {
    const items: SearchResult[] = [];
    const flowsResult = await graphql('listFlowsByWorkspace', { workspaceId });
    if (!flowsResult?.items) return items;

    for (const flow of flowsResult.items as FlowItem[]) {
      if (flow.name) {
        items.push({
          id: flow.id,
          type: 'flow',
          name: flow.name,
          context: 'Flow',
        });
      }
    }

    return items;
  }

  private static async fetchConnections(workspaceId: string): Promise<SearchResult[]> {
    const items: SearchResult[] = [];
    const connectionsResult = await graphql('listConnectionsByWorkspaceNative', { workspaceId });
    if (!connectionsResult?.items) return items;

    for (const conn of connectionsResult.items as ConnectionItem[]) {
      if (conn.name) {
        items.push({
          id: conn.id,
          type: 'connection',
          name: conn.name,
          context: conn.integration?.name || 'Connection',
          integrationName: conn.integration?.name,
        });
      }
    }

    return items;
  }
}
